/*
 * TVG-150: AI Events Assistant (Sales AI POC, events side).
 *
 * Static proof-of-concept data, hard coded straight into the page per the
 * issue scope: no backend, no API, no data file fetch, no calculation. The
 * page mirrors the AI Sales Assistant — ranked rows plus a slide-in detail
 * panel — but points the same engine at live confirmed events.
 *
 * Two tabs ship now:
 *   1. Payments and risk — a ranked list of confirmed weddings whose balance
 *      is overdue, ordered by amount overdue (real-shaped illustrative data).
 *   2. Upsell — two made-up examples, clearly an illustrative placeholder.
 */

/** Risk band for an overdue event. */
export type RiskBand = 'overdue' | 'high-risk';

/** A label/value pair used by the payment breakdown. */
export type Pair = [string, string];

/** A confirmed wedding whose balance is overdue (Tab 1 row + detail). */
export interface OverdueEvent {
  id: number;
  rank: number;
  /** The couple, e.g. "Sophie & James Whitlock". */
  couple: string;
  venue: string;
  /** Human date of the wedding, e.g. "18 July 2026". */
  eventDate: string;
  /** Amount overdue, pre-formatted, e.g. "£12,450". */
  amountOverdue: string;
  /** How many days the payment is past its due date. */
  daysOverdue: number;
  /** Weeks until the wedding day. */
  weeksToDay: number;
  band: RiskBand;
  bandLabel: string;
  /** One-line reason this row is ranked where it is. */
  reason: string;
  /** Full payment breakdown shown in the detail panel. */
  payment: Pair[];
  /** Suggested balance reminder message. */
  reminderSubject: string;
  reminderBody: string;
}

export interface PaymentsMeta {
  title: string;
  subtitle: string;
  /** Header summary line, e.g. "356 payments overdue totalling £784k…". */
  summary: string;
  lastRun: string;
}

/** A single illustrative upsell example (Tab 2). */
export interface UpsellExample {
  id: number;
  couple: string;
  /** What the couple is currently looking at / has booked. */
  current: string;
  /** Their stated budget, pre-formatted. */
  budget: string;
  /** The alternative the assistant suggests. */
  suggestion: string;
  /** Short rationale for the suggestion. */
  rationale: string;
  exampleSubject: string;
  exampleEmail: string;
}

export interface PaymentsTab {
  meta: PaymentsMeta;
  events: OverdueEvent[];
}

export interface UpsellTab {
  /** Where the live version will come from, once built. */
  note: string;
  examples: UpsellExample[];
}

export const PAYMENTS_TAB: PaymentsTab = {
  meta: {
    title: 'AI Events Assistant',
    subtitle: 'Confirmed weddings with an overdue balance, ranked by who to chase first',
    summary:
      '356 payments overdue totalling £784k, and £719k outstanding on 124 confirmed events in the next 60 days.',
    lastRun: '10 June 2026 export',
  },
  events: [
    {
      id: 1,
      rank: 1,
      couple: 'Sophie & James Whitlock',
      venue: 'Plum Park Manor',
      eventDate: '18 July 2026',
      amountOverdue: '£14,820',
      daysOverdue: 41,
      weeksToDay: 5,
      band: 'high-risk',
      bandLabel: 'High risk',
      reason: 'Largest balance overdue and only 5 weeks to the day; final payment 41 days late.',
      payment: [
        ['Grand total', '£28,640'],
        ['Paid to date', '£13,820'],
        ['Outstanding', '£14,820'],
        ['Deposit', '£2,000'],
        ['Amount overdue', '£14,820'],
        ['Balance due', '1 May 2026 (41 days ago)'],
      ],
      reminderSubject: 'Your final balance for 18 July at Plum Park Manor',
      reminderBody:
        'Hi Sophie and James,\n\nWe are getting everything ready for your wedding at Plum Park Manor on 18 July, now just five weeks away. Our records show a balance of £14,820 that was due on 1 May and is still outstanding.\n\nCould you settle this in the next few days so we can confirm all of your final arrangements? If you would like to talk through the payment or spread it, just reply and we will help.\n\nWarm regards,\nThe Plum Park Manor team',
    },
    {
      id: 2,
      rank: 2,
      couple: 'Amara & Daniel Osei',
      venue: 'Ashton Lodge Country House',
      eventDate: '1 August 2026',
      amountOverdue: '£12,310',
      daysOverdue: 27,
      weeksToDay: 7,
      band: 'high-risk',
      bandLabel: 'High risk',
      reason: 'Second largest balance, 27 days late with under two months to go.',
      payment: [
        ['Grand total', '£24,750'],
        ['Paid to date', '£12,440'],
        ['Outstanding', '£12,310'],
        ['Deposit', '£2,000'],
        ['Amount overdue', '£12,310'],
        ['Balance due', '15 May 2026 (27 days ago)'],
      ],
      reminderSubject: 'Your final balance for 1 August at Ashton Lodge Country House',
      reminderBody:
        'Hi Amara and Daniel,\n\nYour wedding at Ashton Lodge Country House on 1 August is getting close. We still have a balance of £12,310 showing as outstanding, which was due on 15 May.\n\nPlease could you arrange payment in the next few days so we can lock in your final details? If anything has changed, reply to this email and we will sort it out together.\n\nWarm regards,\nThe Ashton Lodge team',
    },
    {
      id: 3,
      rank: 3,
      couple: 'Grace & Oliver Bennett',
      venue: 'Redhouse Barn',
      eventDate: '22 August 2026',
      amountOverdue: '£10,940',
      daysOverdue: 19,
      weeksToDay: 10,
      band: 'high-risk',
      bandLabel: 'High risk',
      reason: 'Large balance overdue 19 days; chase before the final-numbers window.',
      payment: [
        ['Grand total', '£21,480'],
        ['Paid to date', '£10,540'],
        ['Outstanding', '£10,940'],
        ['Deposit', '£2,000'],
        ['Amount overdue', '£10,940'],
        ['Balance due', '23 May 2026 (19 days ago)'],
      ],
      reminderSubject: 'Your final balance for 22 August at Redhouse Barn',
      reminderBody:
        'Hi Grace and Oliver,\n\nWe are looking forward to your wedding at Redhouse Barn on 22 August. There is a balance of £10,940 on your account that was due on 23 May.\n\nWhen you have a moment, please could you settle this so everything stays on track for the day? Reply any time if you have a question about the payment.\n\nWarm regards,\nThe Redhouse Barn team',
    },
    {
      id: 4,
      rank: 4,
      couple: 'Priya & Tom Halloran',
      venue: 'The Great Barn',
      eventDate: '5 September 2026',
      amountOverdue: '£9,260',
      daysOverdue: 33,
      weeksToDay: 12,
      band: 'high-risk',
      bandLabel: 'High risk',
      reason: 'Overdue 33 days — the longest in the top group — despite more runway.',
      payment: [
        ['Grand total', '£19,200'],
        ['Paid to date', '£9,940'],
        ['Outstanding', '£9,260'],
        ['Deposit', '£2,000'],
        ['Amount overdue', '£9,260'],
        ['Balance due', '9 May 2026 (33 days ago)'],
      ],
      reminderSubject: 'Your final balance for 5 September at The Great Barn',
      reminderBody:
        'Hi Priya and Tom,\n\nYour wedding at The Great Barn on 5 September is in the diary and we cannot wait. Our records show £9,260 still outstanding, originally due on 9 May.\n\nCould you arrange payment this week so we can keep everything moving? If you would prefer to pay in instalments, just let us know and we will set that up.\n\nWarm regards,\nThe Great Barn team',
    },
    {
      id: 5,
      rank: 5,
      couple: 'Hannah & Lewis Carrick',
      venue: 'Milling Barn',
      eventDate: '12 September 2026',
      amountOverdue: '£7,580',
      daysOverdue: 14,
      weeksToDay: 13,
      band: 'overdue',
      bandLabel: 'Overdue',
      reason: 'Mid-sized balance, two weeks late; routine reminder.',
      payment: [
        ['Grand total', '£16,420'],
        ['Paid to date', '£8,840'],
        ['Outstanding', '£7,580'],
        ['Deposit', '£2,000'],
        ['Amount overdue', '£7,580'],
        ['Balance due', '28 May 2026 (14 days ago)'],
      ],
      reminderSubject: 'Your final balance for 12 September at Milling Barn',
      reminderBody:
        'Hi Hannah and Lewis,\n\nWe hope the wedding planning is going well. A quick note that your balance of £7,580 for 12 September at Milling Barn was due on 28 May and is still showing as outstanding.\n\nPlease could you settle this when you get a chance? Reply to this email if you have any questions.\n\nWarm regards,\nThe Milling Barn team',
    },
    {
      id: 6,
      rank: 6,
      couple: 'Eve & Marcus Delaney',
      venue: 'Plum Park Manor',
      eventDate: '26 September 2026',
      amountOverdue: '£6,140',
      daysOverdue: 9,
      weeksToDay: 15,
      band: 'overdue',
      bandLabel: 'Overdue',
      reason: 'Recently overdue; gentle first reminder.',
      payment: [
        ['Grand total', '£15,300'],
        ['Paid to date', '£9,160'],
        ['Outstanding', '£6,140'],
        ['Deposit', '£2,000'],
        ['Amount overdue', '£6,140'],
        ['Balance due', '2 June 2026 (9 days ago)'],
      ],
      reminderSubject: 'Your final balance for 26 September at Plum Park Manor',
      reminderBody:
        'Hi Eve and Marcus,\n\nJust a friendly reminder that your balance of £6,140 for your wedding at Plum Park Manor on 26 September was due on 2 June.\n\nWhen you have a moment, please could you settle it? Do reply if you have any questions about your payment.\n\nWarm regards,\nThe Plum Park Manor team',
    },
    {
      id: 7,
      rank: 7,
      couple: 'Niamh & Charlie Frost',
      venue: 'Redhouse Barn',
      eventDate: '10 October 2026',
      amountOverdue: '£4,720',
      daysOverdue: 6,
      weeksToDay: 17,
      band: 'overdue',
      bandLabel: 'Overdue',
      reason: 'Smaller balance, only a few days late.',
      payment: [
        ['Grand total', '£13,880'],
        ['Paid to date', '£9,160'],
        ['Outstanding', '£4,720'],
        ['Deposit', '£2,000'],
        ['Amount overdue', '£4,720'],
        ['Balance due', '5 June 2026 (6 days ago)'],
      ],
      reminderSubject: 'Your final balance for 10 October at Redhouse Barn',
      reminderBody:
        'Hi Niamh and Charlie,\n\nA quick note that your balance of £4,720 for 10 October at Redhouse Barn was due on 5 June.\n\nPlease could you settle this when convenient? Reply any time if you would like to talk it through.\n\nWarm regards,\nThe Redhouse Barn team',
    },
    {
      id: 8,
      rank: 8,
      couple: 'Isla & Ben Mortimer',
      venue: 'The Great Barn',
      eventDate: '17 October 2026',
      amountOverdue: '£3,310',
      daysOverdue: 3,
      weeksToDay: 18,
      band: 'overdue',
      bandLabel: 'Overdue',
      reason: 'Just tipped overdue; lowest balance in the list.',
      payment: [
        ['Grand total', '£12,650'],
        ['Paid to date', '£9,340'],
        ['Outstanding', '£3,310'],
        ['Deposit', '£2,000'],
        ['Amount overdue', '£3,310'],
        ['Balance due', '8 June 2026 (3 days ago)'],
      ],
      reminderSubject: 'Your final balance for 17 October at The Great Barn',
      reminderBody:
        'Hi Isla and Ben,\n\nJust a gentle reminder that your balance of £3,310 for your wedding at The Great Barn on 17 October was due on 8 June.\n\nWhenever you are ready, please could you settle it? We are here if you have any questions.\n\nWarm regards,\nThe Great Barn team',
    },
  ],
};

export const UPSELL_TAB: UpsellTab = {
  note:
    'Illustrative placeholder, not live data. The real version — 180 confirmed events sitting below their minimum spend, with £916k of combined headroom — comes later.',
  examples: [
    {
      id: 1,
      couple: 'Lucy & Aaron Pike',
      current: 'Saturday 8 May 2027 at Plum Park Manor, peak-season Saturday rate',
      budget: '£14,000',
      suggestion: 'The same week as a Friday, or late March, both inside their budget',
      rationale:
        'Their quote sits about £3,200 above budget on a peak Saturday. A Friday in the same week, or a Sunday in late March, lands inside £14,000 while keeping Plum Park Manor.',
      exampleSubject: 'A couple of options to bring your Plum Park Manor wedding into budget',
      exampleEmail:
        'Hi Lucy and Aaron,\n\nWe would love to host your wedding at Plum Park Manor. Looking at your numbers, a peak Saturday in May comes in a little above the £14,000 you had in mind.\n\nTwo options keep the same venue and bring it within budget: a Friday in that same May week, or a Sunday in late March. Both would save you around £3,000 while giving you the full Plum Park Manor experience.\n\nWould you like me to hold one of these dates while you decide?\n\nWarm regards,\nThe Plum Park Manor team',
    },
    {
      id: 2,
      couple: 'Megan & Sam Whitfield',
      current: 'Enquiring about a Saturday at Ashton Lodge Country House',
      budget: '£11,500',
      suggestion: 'Redhouse Barn on their preferred date, or Ashton Lodge in off-peak months',
      rationale:
        'Ashton Lodge on a summer Saturday is about £2,800 over budget. Redhouse Barn on the same date fits comfortably, as does Ashton Lodge itself in November to March.',
      exampleSubject: 'An alternative that fits your budget for your wedding',
      exampleEmail:
        'Hi Megan and Sam,\n\nThank you for thinking of us for your wedding. A summer Saturday at Ashton Lodge Country House comes in around £2,800 above your £11,500 budget.\n\nIf you love that date, Redhouse Barn is available then and sits comfortably within budget, with the same all-in service. If your heart is set on Ashton Lodge, the November to March dates would also bring it within range.\n\nHappy to talk either through — just let me know which appeals and I will hold it for you.\n\nWarm regards,\nThe Venue Group team',
    },
  ],
};
